<template>
  <div class="map-container">
    <div
      class="overlay-container"
      :style="{ height: getMapHeight }"
    >
      <div
        v-if="loading"
        class="loading-spinner"
      >
        <loading-logo />
      </div>
      <l-map
        ref="map"
        :zoom="zoom"
        :center="center"
        :style="{ height: getMapHeight, width: '100%', position: 'relative' }"
        @update:center="updateCenter"
        @ready="onMapReady"
      >
        <!-- Button for toggling full screen -->
        <b-button
          v-if="!fullScreen"
          variant="primary"
          class="mb-1 p-0 map-button"
          @click="toggleFullScreen"
        >
          <feather-icon
            icon="Maximize2Icon"
            size="24"
          />
        </b-button>

        <!-- Button for exiting full screen -->
        <b-button
          v-else
          variant="primary"
          class="mb-1 p-0 map-button"
          @click="toggleFullScreen"
        >
          <feather-icon
            icon="Minimize2Icon"
            size="24"
          />
        </b-button>

        <l-tile-layer :url="url" />
        <template v-if="transformedPositions.length">
          <l-marker
            v-for="(position, index) in transformedPositions"
            :key="index"
            :lat-lng="position.coords"
            :icon="index === lastPositionIndex ? defaultIcon : secondaryIcon"
          >
            <l-popup :class="'custom-popup'">
              <div class="tooltip-content">
                <strong>Fecha:</strong> {{ formatDate(position.date) }}<br>
                <small>Coords: {{ position.coords }}</small>
              </div>
            </l-popup>
            <l-tooltip>
              <div class="tooltip-content">
                <strong>Fecha:</strong> {{ formatDate(position.date) }}<br>
                <small>Coords: {{ position.coords }}</small>
              </div>
            </l-tooltip>
          </l-marker>
          <l-polyline
            :lat-lngs="transformedPositions.map(p => p.coords)"
            :color="secondaryColor"
          />
        </template>
        <template v-else>
          <div class="no-data-message">
            No hay datos disponibles
          </div>
        </template>
      </l-map>
    </div>

    <!-- Full screen mode -->
    <b-card-code
      v-if="fullScreen"
      class="full-screen-map"
    >
      <l-map
        ref="fullScreenMap"
        :zoom="zoom"
        :center="center"
        :style="{ height: getMapHeight, width: '100%', position: 'relative' }"
        @update:center="updateCenter"
        @ready="onMapReady"
      >
        <!-- Button for toggling full screen -->
        <b-button
          v-if="!fullScreen"
          variant="primary"
          class="mb-1 p-0 map-button"
          @click="toggleFullScreen"
        >
          <feather-icon
            icon="Maximize2Icon"
            size="24"
          />
        </b-button>

        <!-- Button for exiting full screen -->
        <b-button
          v-else
          variant="primary"
          class="mb-1 p-0 map-button"
          @click="toggleFullScreen"
        >
          <feather-icon
            icon="Minimize2Icon"
            size="24"
          />
        </b-button>

        <l-tile-layer :url="url" />
        <template v-if="transformedPositions.length">
          <l-marker
            v-for="(position, index) in transformedPositions"
            :key="index"
            :lat-lng="position.coords"
            :icon="index === lastPositionIndex ? defaultIcon : secondaryIcon"
          >
            <l-popup :class="'custom-popup'">
              <div class="tooltip-content">
                <strong>Fecha:</strong> {{ formatDate(position.date) }}<br>
                <small>Coords: {{ position.coords }}</small>
              </div>
            </l-popup>
            <l-tooltip>
              <div class="tooltip-content">
                <strong>Fecha:</strong> {{ formatDate(position.date) }}<br>
                <small>Coords: {{ position.coords }}</small>
              </div>
            </l-tooltip>
          </l-marker>
          <l-polyline
            :lat-lngs="transformedPositions.map(p => p.coords)"
            :color="secondaryColor"
          />
        </template>
        <template v-else>
          <div class="no-data-message">
            No hay datos disponibles
          </div>
        </template>
      </l-map>
    </b-card-code>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import BCardCode from '@core/components/b-card-code/BCardCode.vue'
import {
  LMap, LTileLayer, LMarker, LPolyline, LPopup, LTooltip,
} from 'vue2-leaflet'
import { Icon } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import moment from 'moment-timezone'
import store from '@/store'
import LoadingLogo from '@/views/miscellaneous/LoadingLogo.vue'

// eslint-disable-next-line no-underscore-dangle
delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

const createCustomIcon = (color, opacity, size = [25, 41]) => new Icon({
  iconUrl: `data:image/svg+xml;base64,${btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 41" width="${size[0]}" height="${size[1]}">
      <path fill="${color}" fill-opacity="${opacity}" d="M12.5 0C5.6 0 0 5.6 0 12.5 0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0zM12.5 19c-3.6 0-6.5-2.9-6.5-6.5S8.9 6 12.5 6 19 8.9 19 12.5 16.1 19 12.5 19z"/>
    </svg>
  `)}`,
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: size,
  iconAnchor: [size[0] / 2, size[1]],
  popupAnchor: [1, -size[1] + 10],
  shadowSize: [41, 41],
})

const defaultIcon = new Icon({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
})

const secondaryColor = '#4a4a4a' // Un color más oscuro
const secondaryIcon = createCustomIcon(secondaryColor, 0.6, [20, 33]) // Tamaño más pequeño

export default {
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPolyline,
    LPopup,
    LTooltip,
    BButton,
    BCardCode,
    LoadingLogo,
  },
  props: {
    positions: {
      type: Array,
      required: true,
    },
    mapHeight: {
      type: String,
      default: '150px',
    },
    mapFullScreenSize: {
      type: String,
      default: '75vh',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      zoom: 10,
      center: this.positions.length ? this.positions[0].y : [0, 0],
      fullScreen: false,
      defaultIcon,
      secondaryIcon,
      secondaryColor,
    }
  },
  computed: {
    getMapHeight() {
      return this.fullScreen ? this.$props.mapFullScreenSize : this.$props.mapHeight
    },
    transformedPositions() {
      return this.positions.map(position => ({
        coords: position.y,
        date: new Date(position.x),
        info: `Date: ${this.formatDate(new Date(position.x))}<br><small>Coords: ${position.y}</small>`, // Example info, you can customize this
      }))
    },
    lastPositionIndex() {
      if (this.transformedPositions.length === 0) return -1
      let latestIndex = 0
      let latestDate = this.transformedPositions[0].date
      this.transformedPositions.forEach((position, index) => {
        if (position.date > latestDate) {
          latestDate = position.date
          latestIndex = index
        }
      })
      return latestIndex
    },
    isDarkMode() {
      return store.state.appConfig.layout.skin === 'dark'
    },
  },
  watch: {
    positions(newPositions) {
      if (newPositions.length === 0) {
        this.center = [0, 0]
      } else {
        this.center = newPositions[0].y
      }
      this.$nextTick(() => {
        this.invalidateMapSize()
      })
    },
    fullScreen() {
      this.$nextTick(() => {
        this.invalidateMapSize()
      })
    },
  },
  methods: {
    toggleFullScreen() {
      this.fullScreen = !this.fullScreen
      this.$nextTick(() => {
        this.invalidateMapSize()
      })
    },
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY hh:mm:ss a')
    },
    updateCenter(newCenter) {
      this.center = newCenter
    },
    onMapReady() {
      this.invalidateMapSize()
    },
    invalidateMapSize() {
      if (this.$refs.map && this.$refs.map.mapObject) {
        this.$refs.map.mapObject.invalidateSize()
      }
      if (this.fullScreen && this.$refs.fullScreenMap && this.$refs.fullScreenMap.mapObject) {
        this.$refs.fullScreenMap.mapObject.invalidateSize()
      }
    },
  },
  mounted() {
    window.addEventListener('resize', this.invalidateMapSize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.invalidateMapSize)
  },
}
</script>

<style lang="scss">
.map-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.overlay-container {
  position: relative;
  width: 100%;
}

.loading-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1001;
}

.vue2leaflet-map {
  &.leaflet-container {
    height: 300px;
  }
}

.full-screen-map {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80vw;
  height: fit-content;
  z-index: 10000; /* Ensure it's above all other elements */
}

.map-button {
  position: absolute;
  bottom: 10px;
  right: 12px;
  z-index: 10001; /* Ensure buttons are above the map */
}

.tooltip-content {
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  border-radius: 4px;
  padding: 5px;
  font-size: 12px;
}

.tooltip-content strong {
  font-size: 14px;
}

.tooltip-content small {
  font-size: 12px;
  color: gray;
}

.custom-popup .leaflet-popup-content-wrapper {
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  border-radius: 4px;
  padding: 5px;
}

.custom-popup .leaflet-popup-tip {
  background-color: rgba(0, 0, 0, 0.8);
}

.no-data-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(255, 255, 255, 0.8);
  padding: 10px;
  border-radius: 4px;
  font-size: 16px;
  color: #333;
  z-index: 1001;
}
</style>